/**
 * U0 · the inspector for a LOOP — the mock's three questions about a repeat.
 *
 *   What repeats            the collection: over · expected · the fact it derives from · the body
 *   At the merge            AGGREGATED · RETAINED PER <x> · BLOCKS CLOSURE — all derived (R-20)
 *   Advanced                concurrency · nesting · closure
 *
 * Nothing about the merge is configured here. It is read off each rule's scope by the engine
 * and arrives as `derived["loop:<code>#merge"]`; the form renders the fact and says where it
 * came from. A client that worked it out for itself would be deriving (RV0 A), and the merge
 * is precisely the part that surprises people — hiding it is how a QA author ships a record
 * believing per-tub budgets aggregate (U0 §3).
 */
import React, { useId, useMemo, useState } from "react";
import { Checkbox, Flex, Input, InputNumber, Radio, Select, Typography } from "antd";
import { GitMerge, Layers, Repeat, SlidersHorizontal } from "lucide-react";
import { CheckRowFields } from "./steps/CaptureForm";

const { Text } = Typography;

const ICON = { what: Repeat, merge: GitMerge, adv: SlidersHorizontal };

function Section({ id, title, sub, open, onToggle, children }) {
  const Icon = ICON[id] || Layers;
  return (
    <section className={`u0-sec ${open ? "open" : ""}`}>
      <button type="button" className="u0-sech" aria-expanded={open} onClick={() => onToggle(id)}>
        <span className="ic"><Icon size={14} strokeWidth={1.8} /></span>
        <span className="t">{title}{sub ? <span className="u0-secsub"> — {sub}</span> : null}</span>
        <span className="cv">▾</span>
      </button>
      {open ? <div className="u0-secb">{children}</div> : null}
    </section>
  );
}

// A <div role="group">, not a <label> — see steps/Field.jsx: a label re-clicks its first control,
// and a Select inside one opened and shut again at once.
function Field({ label, children }) {
  const id = useId();
  return (
    <div className="u0-field" role="group" aria-labelledby={id}>
      <span className="l" id={id}>{label}</span>
      {children}
    </div>
  );
}

const count = (nodes, fn) => {
  let n = 0;
  const walk = (list) => (list || []).forEach((c) => { if (fn(c)) n += 1; walk(c.children); });
  walk(nodes);
  return n;
};

export default function LoopForm({
  step,            // the LOOP node, with payload {over, kind, expected, ref} and children
  operation,       // the operation it sits in — concurrency lives there
  derived = {},    // the envelope's derived facts
  bomLines = [],   // the record's whole bill of materials — the same list the charge step picks from
  masters = { parameters: [] },   // the parameter master — what a rule-bounded repeat measures
  readOnly = false,
  onChange,        // (patch) — over_subject · bound_kind · expected · bound_ref · on_exhausted
  onConcurrency,   // (stations) — set on the operation
}) {
  // 2026-10-23 (owner) · "until a cycle passes click nahi ho raha": an edit is staged in StepForm's draft and
  // arrives as the top-level field it writes (bound_kind, bound_ref, over_subject, expected, on_exhausted),
  // while the saved values live in `payload`. Read the staged one over the saved one, or a choice made here
  // snaps back to the saved value until Save — which read as the option not taking the click.
  const staged = (field, key) => (step && field in step ? { [key]: step[field] } : {});
  const p = {
    ...(step?.payload || {}),
    ...staged("over_subject", "over"), ...staged("over_subject", "over_subject"),
    ...staged("bound_kind", "kind"), ...staged("bound_ref", "ref"),
    ...staged("expected", "expected"), ...staged("on_exhausted", "on_exhausted"),
  };
  // What the toolbox writes into a brand-new repeat so the model will accept it. It is not
  // an answer, and must not be read as one — an author who inserted a repeat and has not
  // said what it is for should be asked, not shown a guess.
  const UNSET = "not stated yet";
  // 2026-10-22 (owner) · "repeated step me count ke saath ek aur option — count nahi rahega, jab tak cycle pass
  // nahi hoti tab tak chalta rahega; each cycle ke baad pop-up me puchega pass hai ya fail". The server's own
  // sentence for it (acts.PASS_FAIL_REF): a rule-bounded repeat whose rule is the operator's Pass / Fail.
  const PASS_FAIL = "Pass / Fail after each cycle";
  const passFail = p.kind === "UNTIL_RULE" && p.ref === PASS_FAIL;

  const merge = derived[`loop:${step?.code}#merge`] || null;
  const collection = derived[`loop:${step?.code}#collection`] || null;
  const v = merge?.value?.variable || collection?.value?.variable || p.over || "item";
  const body = count(step?.children, (c) => c.kind === "STEP");
  const nested = (step?.children || []).flatMap(function find(c) {
    return c.kind === "LOOP" ? [c] : (c.children || []).flatMap(find);
  });
  const stations = operation?.concurrency?.stations || 0;

  const [opened, setOpened] = useState({ what: true, merge: true, adv: false });
  const toggle = (id) => setOpened((o) => ({ ...o, [id]: !o[id] }));
  const set = (patch) => !readOnly && onChange?.(patch);

  const kinds = useMemo(() => [
    { value: "OVER_COLLECTION", label: "Over a collection" },
    { value: "UNTIL_RULE", label: "Until a rule ends it" },
  ], []);

  // What a repeat is FOR. The two answers need different questions, and asking both of them
  // at once — a free-text "over one of what" — let an author write a phrase the server does
  // not recognise and get a loop that silently repeats over nothing.
  //
  // The phrases below are the ones `loop_subjects.SUBJECT_ROLES` maps to bill-of-materials
  // roles. Offering them instead of a text box is the difference between a loop that
  // resolves to four excipients and one that resolves to none.
  // What the bill calls each role, for the table's last column.
  const ROLE_LABEL = {
    API: "API", EXCIPIENT: "Excipient", PH_ADJUST: "pH adjuster", SOLVENT: "Solvent",
    PRIMARY_PM: "Primary packing", SECONDARY_PM: "Secondary packing", PROCESS_AID: "Processing aid",
  };
  // The record's bill of materials — the SAME list a material charge step picks from, and
  // the one the MFR put there.
  //
  // Not `operation.dispensing.materials`: that is the subset already bound to this
  // operation for scheduling, and on a record where nobody has bound them yet it is empty.
  // An author who has written a bill and cannot see it in the repeat that dispenses it has
  // been told, wrongly, that the record holds no materials.
  const bill = useMemo(() => (bomLines || []).map((l) => ({
    sap_code: l.sap_code, material: l.material, role: l.role,
    // The reference already writes the quantity with its unit — "6000 mg", "0.006 kg" —
    // so there is no separate uom to append. The dispensed figure is the one a dispensing
    // loop is about; the per-basis quantity is shown where there is no dispensed one.
    quantity: l.dispense || l.quantity || "",
  })), [bomLines]);

  //: A loop binds to a KIND of material, not to a list of SAP codes. That is the record's
  //: own rule (`loop_subjects.SUBJECT_ROLES`) and it is the right one: a batch that adds an
  //: excipient to the bill should be dispensed by the same loop, not by a record somebody
  //: remembered to re-author. So ticking a material takes its role, and the phrase the
  //: server understands is composed from the roles ticked.
  const ROLE_PHRASE = [
    { roles: ["API", "EXCIPIENT", "PH_ADJUST", "PROCESS_AID"], over: "material of the bill of materials" },
    { roles: ["API", "EXCIPIENT", "PH_ADJUST"], over: "ingredient on the bill of materials" },
    { roles: ["PRIMARY_PM", "SECONDARY_PM", "PROCESS_AID"], over: "packaging material on the bill of materials" },
    { roles: ["PRIMARY_PM", "SECONDARY_PM"], over: "packing material on the bill of materials" },
    { roles: ["EXCIPIENT", "PH_ADJUST"], over: "raw material on the bill of materials" },
    { roles: ["PROCESS_AID"], over: "processing aid on the bill of materials" },
  ];
  //: The server knows six groups and no others (`loop_subjects.SUBJECT_ROLES`), so a set of
  //: roles is answered by the SMALLEST group that covers it — ticking an excipient asks for
  //: "raw material" (excipients and pH adjusters), and also ticking the API widens it to
  //: "ingredient". An exact match is preferred; without this, a set the vocabulary has no
  //: word for silently did nothing, which is how the ticks stopped responding.
  const phraseFor = (roles) => {
    if (!roles.length) return "";
    const want = [...roles].sort().join("|");
    const exact = ROLE_PHRASE.find((r) => [...r.roles].sort().join("|") === want);
    if (exact) return exact.over;
    const covers = ROLE_PHRASE
      .filter((r) => roles.every((x) => r.roles.includes(x)))
      .sort((a, b) => a.roles.length - b.roles.length)[0];
    return covers ? covers.over : "";
  };
  const rolesOf = (over) => {
    const hit = ROLE_PHRASE.find((r) => r.over === over);
    return new Set(hit ? hit.roles : []);
  };
  // Which roles are ticked, held HERE rather than read back off `p.over`. The saved value
  // only changes when the author presses Save, so a tick read from it flipped straight back
  // and the boxes looked dead.
  //
  // A repeat that has not been told what it is over starts with everything a group can
  // cover already ticked: the common case is "dispense all of them", and an author who
  // wants fewer unticks what they do not want rather than building the list up from empty.
  const pickable = useMemo(
    () => [...new Set((bomLines || []).map((l) => l.role)
      .filter((r) => ROLE_PHRASE.some((x) => x.roles.includes(r))))],
    [bomLines]);
  // 2026-10-14 (owner) · "BOM ki list aati hai, usme se jo select karte hai materials vahi aane
  // chahiye." A tick is ONE material (its SAP code), not its whole kind. The roles are still worked
  // out from what is ticked — the server needs the phrase — and the codes go with it
  // (`subject_codes`, loop_subjects.KEY). Every line of a kind ticked = the kind, as before, so a
  // material added to the bill later still joins the loop; fewer = exactly those.
  const canPickLine = (l) => ROLE_PHRASE.some((r) => r.roles.includes(l.role)) && !!l.sap_code;
  const [chosenCodes, setChosenCodes] = useState(() => {
    if ((step?.subject_codes || []).length) return new Set(step.subject_codes);
    const saved = rolesOf(p.over || "");
    const roles = saved.size ? saved : new Set(pickable);
    return new Set((bomLines || []).filter((l) => canPickLine(l) && roles.has(l.role)).map((l) => l.sap_code));
  });
  const chosenRoles = useMemo(
    () => new Set((bomLines || []).filter((l) => chosenCodes.has(l.sap_code)).map((l) => l.role)),
    [bomLines, chosenCodes]);
  const setChosenRoles = (roles) => setChosenCodes(new Set(
    (bomLines || []).filter((l) => canPickLine(l) && roles.has(l.role)).map((l) => l.sap_code)));
  /** What is saved: the codes when the ticks are a subset of the kinds they cover, else none (the kind). */
  const codesToSave = (codes) => {
    const roles = new Set((bomLines || []).filter((l) => codes.has(l.sap_code)).map((l) => l.role));
    const over = phraseFor([...roles]);
    const covered = ROLE_PHRASE.find((r) => r.over === over)?.roles || [];
    const whole = (bomLines || []).filter((l) => canPickLine(l) && covered.includes(l.role));
    return whole.every((l) => codes.has(l.sap_code)) ? [] : [...codes];
  };
  // The record is told what the opening tick-set means, so a repeat left untouched still
  // says what it repeats over rather than carrying the toolbox's "not stated yet".
  //: The lines the ticked roles actually cover — the passes this repeat runs today. The
  //: authoritative count is still derived at issue (a material rejected or split changes
  //: it), but an author ticking "excipient" should see 2 rather than "unknown until issue"
  //: while two excipients sit on the screen above.
  const picked = useMemo(
    () => (bomLines || []).filter((l) => chosenCodes.has(l.sap_code)),
    [bomLines, chosenCodes]);

  //: A rule-bounded repeat ends on a READING against a range, so it is composed from the
  //: parameter master and written into `bound_ref` as one sentence the record can print.
  //: `bound_ref` stays a sentence — that is what the model holds — but it is now built from
  //: a parameter the plant has, with its own unit, rather than typed from memory.
  const ruleText = (name, unit, lo, hi) => {
    const u = unit ? ` ${unit}` : "";
    if (lo && hi) return `${name} ${lo}–${hi}${u} reached`;
    if (lo) return `${name} not below ${lo}${u}`;
    if (hi) return `${name} not above ${hi}${u}`;
    return `${name} within range`;
  };
  // Read back off the saved sentence so re-opening a repeat shows what it was set to.
  const savedRule = useMemo(() => {
    const ref = p.ref || "";
    const m = (masters?.parameters || [])
      .filter((x) => ref.startsWith(x.name))
      .sort((a, b) => b.name.length - a.name.length)[0];
    if (!m) return { param: null, lo: "", hi: "" };
    const nums = ref.slice(m.name.length).match(/-?\d+(?:\.\d+)?/g) || [];
    return { param: { id: m.id, name: m.name, unit: m.unit || "" },
             lo: nums[0] || "", hi: nums[1] || "" };
  }, [p.ref, masters]);
  const [ruleParam, setRuleParam] = useState(savedRule.param);
  const [ruleMin, setRuleMin] = useState(savedRule.lo);
  const [ruleMax, setRuleMax] = useState(savedRule.hi);

  const toggleLine = (code) => {
    const next = new Set(chosenCodes);
    if (next.has(code)) next.delete(code); else next.add(code);
    setChosenCodes(next);                        // the box answers the press, always
    const roles = new Set((bomLines || []).filter((l) => next.has(l.sap_code)).map((l) => l.role));
    const over = phraseFor([...roles]);
    if (!over) {
      // Nothing ticked is a real state: the author has not said what this repeats over,
      // and the readiness check reports it until they do. It is written as UNSET rather
      // than blank because the model refuses a LOOP with no subject at all.
      set({ over_subject: UNSET, bound_ref: UNSET, subject_codes: [] });
      return;
    }
    set({
      over_subject: over, bound_kind: "OVER_COLLECTION", expected: null,
      // The count derives from the bill when the batch is issued. Saying so is what the
      // model asks for — a collection loop states a count or names the fact behind it.
      bound_ref: "the bill of materials, at issue",
      subject_codes: codesToSave(next),
    });
  };

  // A repeat is a material one when the SAVED subject is a phrase the bill resolves. Read
  // off the tick-set instead, every new repeat would open as a dispensing one, because the
  // ticks default to on — and an author who inserted a repeat to count vessels would find
  // it bound to the bill of materials.
  const isMaterial = rolesOf(p.over || "").size > 0;
  const answered = !!p.over && p.over !== UNSET;
  // A repeat with nothing stated yet is neither, and must not be guessed into one: an
  // author who opened it for a count should not find it bound to the bill of materials.
  // 2026-10-30 (owner) · a check row: opened by a trigger (ON_TRIGGER), with its trigger, heads and due window.
  const checkRow = step?.check_row || null;
  const [purpose, setPurpose] = useState(
    p.kind === "ON_TRIGGER" ? "check" : !answered ? "" : isMaterial ? "material" : p.kind === "UNTIL_RULE" && !passFail ? "until" : "count");

  if (!step) return null;
  return (
    <Flex vertical gap={10} className="u0-stepform">
      <div className="u0-stepid" data-family="structure">
        <div className="u0-stepid-title" style={{ cursor: "default" }}>
          {purpose === "check" ? `Check row — ${answered ? p.over : "—"}` : `Repeat for each ${answered ? p.over : "—"}`}
        </div>
        {purpose === "check" && checkRow?.label ? <span className="dz-chip">{checkRow.label}</span> : null}
        <div className="u0-stepid-row">
          <span className="dz-code">{step.code}</span>
          <span className="pos">{operation?.name}{p.kind ? ` · ${p.kind}` : ""}</span>
        </div>
      </div>

      <Section id="what" title="What repeats" sub={`${body} step${body === 1 ? "" : "s"} in the body`}
               open={opened.what} onToggle={toggle}>
        <Field label="What is this repeat for">
          <Select size="small" value={purpose || undefined} disabled={readOnly}
                  placeholder="Choose what it repeats over"
                  options={[
                    { value: "material", label: "Dispensing — once per material on the bill" },
                    { value: "count", label: "A repeated step — a stated number of times" },
                    { value: "until", label: "Until a reading is right — repeat while it is not" },
                    { value: "check", label: "A check row — opened by a trigger" },
                  ]}
                  onChange={(k) => {
                    setPurpose(k);
                    // Switching purpose clears the other one's answer rather than leaving a
                    // stale phrase behind a count, or a count behind a phrase.
                    if (k === "check") {
                      setChosenRoles(new Set());
                      // What is checked is the author's to type; nothing is written for it until they do.
                      set({ bound_kind: "ON_TRIGGER", bound_ref: "", expected: null, subject_codes: [],
                            check_row: { ...(checkRow || {}) } });
                      return;
                    }
                    if (checkRow) set({ check_row: null });
                    if (k === "material") {
                      // Dispensing opens with everything the bill offers already ticked —
                      // "all of them" is the common answer — and the record is told so at
                      // once, rather than sitting on the toolbox's "not stated yet" until
                      // the author happens to touch a box.
                      const all = new Set(pickable);
                      setChosenRoles(all);
                      setRuleParam(null); setRuleMin(""); setRuleMax("");
                      const over = phraseFor([...all]);
                      set(over
                        ? { over_subject: over, bound_kind: "OVER_COLLECTION", expected: null,
                            bound_ref: "the bill of materials, at issue", subject_codes: [] }
                        : { over_subject: "", expected: null, bound_kind: "OVER_COLLECTION", subject_codes: [] });
                    }
                    else if (k === "until") {
                      setChosenRoles(new Set());
                      setRuleParam(null); setRuleMin(""); setRuleMax("");
                      set({ over_subject: UNSET, bound_kind: "UNTIL_RULE",
                            bound_ref: UNSET, expected: null, subject_codes: [] });
                    } else {
                      setChosenRoles(new Set());
                      set({ over_subject: UNSET, bound_kind: "OVER_COLLECTION",
                            bound_ref: UNSET, expected: null, subject_codes: [] });
                    }
                  }} />
        </Field>
        {purpose === "material" ? (
          <Field label="Which materials — from the bill of materials">
            {bill.length ? (
              <div className="u0-rowlist">
                <table className="u0-bomtable">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }} />
                      <th>Material</th>
                      <th>SAP code</th>
                      <th style={{ textAlign: "right" }}>Quantity</th>
                      <th>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bill.map((m) => {
                      const on = chosenCodes.has(m.sap_code);
                      // A role no group covers cannot be repeated over — water for injection
                      // is charged, not dispensed line by line. Saying so is better than a
                      // tick that does nothing when pressed.
                      const canPick = canPickLine(m);
                      return (
                        <tr key={m.sap_code || m.material} className={on ? "on" : ""}>
                          <td>
                            <Checkbox checked={on} disabled={readOnly || !canPick}
                                      title={canPick ? undefined
                                        : `${ROLE_LABEL[m.role] || m.role} is not dispensed one line at a time`}
                                      onChange={() => toggleLine(m.sap_code)} />
                          </td>
                          <td>{m.material}</td>
                          <td><span className="dz-code">{m.sap_code}</span></td>
                          <td style={{ textAlign: "right" }}>
                            {m.quantity}
                          </td>
                          <td><span className="u0-rowlist-empty">{ROLE_LABEL[m.role] || m.role}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
                  Only the ticked materials are dispensed in this loop. With every line of a kind
                  ticked the loop binds to the kind, so a material of that kind added to the bill
                  later joins it; untick one and the loop runs over exactly the ticked lines.
                </Text>
              </div>
            ) : (
              <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
                This record has no bill of materials yet. Bring it from the MFR in the Bill of
                materials panel, and its lines appear here to pick from.
              </Text>
            )}
          </Field>
        ) : (purpose === "count" || purpose === "until" || purpose === "check") ? (
          <Field label={purpose === "check" ? "Check" : "Over — one of what"}>
            <Input size="small" defaultValue={answered ? p.over : ""} key={`o-${step.code}`} disabled={readOnly}
                   placeholder={purpose === "check" ? "fill weight check" : "tub, filter, sterilization load"}
                   onBlur={(e) => e.target.value !== (p.over || "") && set({ over_subject: e.target.value })} />
          </Field>
        ) : null}
        {purpose === "count" ? (
          <Field label="How it ends">
            <Radio.Group size="small" disabled={readOnly} value={passFail ? "pass_fail" : "count"}
                         onChange={(e) => set(e.target.value === "pass_fail"
                           ? { bound_kind: "UNTIL_RULE", bound_ref: PASS_FAIL, expected: null,
                               over_subject: answered ? p.over : "cycle" }
                           : { bound_kind: "OVER_COLLECTION", bound_ref: UNSET, expected: null })}
                         options={[
                           { value: "count", label: "A stated number of times" },
                           { value: "pass_fail", label: "Until a cycle passes — Pass / Fail after each" },
                         ]} />
            {passFail ? (
              <Text type="secondary" style={{ display: "block", fontSize: "calc(12.5px * var(--dz-fs, 1))", marginTop: 4 }}>
                No count. After each cycle the operator is asked Pass or Fail: Fail starts the next cycle, Pass finishes the repeat.
              </Text>
            ) : null}
          </Field>
        ) : null}
        {purpose === "check" ? (
          <CheckRowFields cfg={checkRow || {}} readOnly={readOnly} onChange={(c) => set({ check_row: c })} />
        ) : null}
        {passFail || purpose === "check" ? null : (
        <div className="u0-grid2">
          <Field label="Bounded by">
            <Select size="small" value={p.kind || undefined} options={kinds}
                    disabled={readOnly || !!purpose}
                    onChange={(k) => set({ bound_kind: k })} />
          </Field>
          <Field label={purpose === "material" ? "Expected — from the bill"
                        : purpose === "until" ? "At most — before it is escalated"
                        : "How many times"}>
            <InputNumber size="small" min={1} value={p.expected ?? null}
                         disabled={readOnly || purpose === "material"} style={{ width: "100%" }}
                         placeholder={purpose === "material"
                           ? "counted from the bill at issue"
                           : purpose === "until" ? "the most passes allowed"
                           : "state the number"}
                         onChange={(n) => set(
                           // Stating a count answers what `bound_ref` was standing in for,
                           // so the placeholder goes with it rather than being left behind
                           // as "count from not stated yet at issue" on the row.
                           n && p.ref === UNSET ? { expected: n, bound_ref: "" } : { expected: n ?? null })} />
          </Field>
        </div>
        )}
        {purpose === "until" ? (
          <>
            <Field label="Which reading decides">
              <Select size="small" showSearch optionFilterProp="label" disabled={readOnly}
                      value={ruleParam?.id ?? undefined}
                      placeholder="Choose the parameter the body measures"
                      options={(masters?.parameters || []).map((m) => ({
                        value: m.id,
                        label: `${m.name}${m.unit ? ` (${m.unit})` : ""}`,
                      }))}
                      onChange={(id) => {
                        const m = (masters?.parameters || []).find((x) => x.id === id);
                        if (!m) return;
                        setRuleParam({ id, name: m.name, unit: m.unit || "" });
                        set({ bound_ref: ruleText(m.name, m.unit, ruleMin, ruleMax) });
                      }} />
            </Field>
            <div className="u0-grid2">
              <Field label={`Not below${ruleParam?.unit ? ` (${ruleParam.unit})` : ""}`}>
                <Input size="small" value={ruleMin} disabled={readOnly || !ruleParam}
                       placeholder="7.30"
                       onChange={(e) => {
                         setRuleMin(e.target.value);
                         if (ruleParam) set({ bound_ref: ruleText(ruleParam.name, ruleParam.unit, e.target.value, ruleMax) });
                       }} />
              </Field>
              <Field label={`Not above${ruleParam?.unit ? ` (${ruleParam.unit})` : ""}`}>
                <Input size="small" value={ruleMax} disabled={readOnly || !ruleParam}
                       placeholder="7.50"
                       onChange={(e) => {
                         setRuleMax(e.target.value);
                         if (ruleParam) set({ bound_ref: ruleText(ruleParam.name, ruleParam.unit, ruleMin, e.target.value) });
                       }} />
              </Field>
            </div>
            <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
              The reading is taken by a step inside the body, and the pass repeats while it
              sits outside this range. Choosing the parameter here is what ties the two
              together — the body measures it, this decides when to stop.
              {p.ref ? <><br /><b>Reads as:</b> {p.ref}</> : null}
            </Text>
          </>
        ) : purpose === "material" || passFail || purpose === "check" ? null : (
        /* 2026-10-23 · not on "until a cycle passes": it has no count to derive, and this box writes the same
           bound_ref the Pass / Fail choice is stored in — typing here would undo the choice. */
        <Field label="Or the fact the count derives from, at issue">
          <Input size="small" defaultValue={p.ref === UNSET ? "" : p.ref} key={`r-${step.code}`} disabled={readOnly}
                 placeholder="tubs filled, from the per-tub counts at filling"
                 onBlur={(e) => e.target.value !== (p.ref || "") && set({ bound_ref: e.target.value })} />
        </Field>)}
        {purpose === "until" || purpose === "count" ? (
          <Field label="Wait between passes (optional)">
            <Input size="small" defaultValue={p.interval} key={`i-${step.code}`} disabled={readOnly}
                   placeholder="15 minutes between readings"
                   onBlur={(e) => e.target.value !== (p.interval || "")
                                  && set({ payload: { ...(p || {}), interval: e.target.value } })} />
          </Field>
        ) : null}
        {purpose === "material" || passFail || purpose === "check" ? null : (
          <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
            A binding, not a count. If a {v} is rejected or split the expected figure changes
            and the record still closes. The expected figure is what the operator is told,
            never what the engine trusts.
          </Text>
        )}
        <div className="u0-facts">
          <div className="u0-kv"><span className="k">Steps that repeat</span><span className="v">{body}</span></div>
          <div className="u0-kv"><span className="k">Count</span>
            <span className="v">
              {collection?.value?.count
                ?? (purpose === "material"
                  ? (picked.length ? `${picked.length} on the bill today` : "nothing ticked yet")
                  : passFail ? "until a cycle passes — Pass / Fail after each cycle"
                  : purpose === "check" ? "one per trigger, counted when the operation ends"
                  : purpose === "until"
                    ? `until ${p.ref || "the reading is right"}${p.expected ? ` · at most ${p.expected}` : ""}`
                    : p.expected ? `${p.expected} times` : "unknown until issue")}
            </span></div>
        </div>
        {/* WHICH ones. The words above say what the loop repeats over; these are the lines of
            the bill of materials they resolve to — the same list the operator picks a pass
            from. An author could read "each primary packing material or processing aid on the
            bill of materials" and have no way to check it against the four items it means. */}
        {purpose === "material" && (step.subjects || []).length ? (
          <div className="u0-rowlist" style={{ marginTop: 8 }}>
            <b>{step.subjects.length} on the bill of materials · one pass each</b>
            <ol>
              {step.subjects.map((s) => (
                <li key={s.ref}>
                  {s.label}
                  <span className="dz-code" style={{ marginLeft: 6 }}>{s.sap_code}</span>
                  {s.quantity ? <span> · {s.quantity}</span> : null}
                  {s.area ? <span className="u0-rowlist-empty"> · {s.area}</span> : null}
                </li>
              ))}
            </ol>
          </div>
        ) : purpose === "check" ? null : (
          <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
            This loop resolves to no bill-of-materials line — it repeats over something the
            record counts rather than something it lists, and a pass is opened by scanning.
          </Text>
        )}

        {/* The operation's own bill, beneath the loop's resolved subjects. The list above is
            what THIS loop binds to (derived from its over_subject against the bill); this is
            what the operation dispenses as a whole, so an author can see the two agree without
            leaving the loop for the operation panel. Shown only when it would say something the
            list above does not — when the loop resolved to nothing, or to a different number of
            lines. Today's list, never the count: the binding is what the engine trusts, and a
            material rejected at issue changes this list and not the record. */}
        {purpose === "material" && (operation?.dispensing?.materials || []).length
          && (step.subjects || []).length !== operation.dispensing.materials.length ? (
          <Flex vertical gap={2} style={{ marginTop: 8 }}>
            <span className="u0-eyebrow">On the bill today — {operation.dispensing.materials.length}</span>
            {operation.dispensing.materials.map((m) => (
              <div key={m.sap_code || m.material} className="u0-kv">
                <span className="k dz-code">{m.sap_code}</span>
                <span className="v">{m.material}
                  {m.quantity ? <span className="dz-chip" style={{ marginLeft: 6 }}>
                    {m.quantity}{m.uom ? ` ${m.uom}` : ""}</span> : null}</span>
              </div>
            ))}
          </Flex>
        ) : null}
      </Section>

      <Section id="merge" title="At the merge"
               sub={merge?.value?.count ? `after ${merge.value.count} iterations` : "when the loop completes"}
               open={opened.merge} onToggle={toggle}>
        {merge ? (
          <div className="u0-merge">
            <div className="u0-mrow">
              <span className="k">Aggregated</span>
              <span className="v">{merge.value.aggregated?.length ? merge.value.aggregated.join(" · ")
                                   : <i>nothing — no step after the loop records a value</i>}</span>
            </div>
            <div className="u0-mrow">
              <span className="k">Retained per {v}</span>
              <span className="v">{merge.value.retained?.length ? merge.value.retained.join(" · ")
                                   : <i>nothing — no rule inside is scoped to the {v}</i>}</span>
            </div>
            <div className="u0-mrow">
              <span className="k">Blocks closure</span>
              <span className="v">{merge.value.blocks_closure}</span>
            </div>
            <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
              {merge.reason?.text || merge.text} Derived from each rule's scope — nothing is configured here.
            </Text>
          </div>
        ) : (
          <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>The merge has not been derived for this loop.</Text>
        )}
      </Section>

      <Section id="adv" title="Advanced" sub="concurrency · nesting · closure"
               open={opened.adv} onToggle={toggle}>
        <Field label="Concurrency — stations running this operation at once">
          <Flex align="center" gap={8}>
            <InputNumber size="small" min={0} value={stations} disabled={readOnly || !onConcurrency}
                         onChange={(n) => onConcurrency?.(n ?? 0)} style={{ width: 90 }} />
            <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
              {stations >= 2
                ? `Across iterations, parallel. Within one, ordered. Up to ${stations} ${v}s are in hand at once, each with one person; inside a ${v} you cannot classify before inspecting. How many people were actually on the line is recorded at execution.`
                : "One at a time. Set a count when several stations work different iterations together — every rule inside must then say what it is scoped to."}
            </Text>
          </Flex>
        </Field>
        <Field label="Nesting">
          {nested.length ? nested.map((n) => (
            <div key={n.code} className="u0-kv">
              <span className="k">{n.code}</span>
              <span className="v">over {n.payload?.over || "—"} — closes into its {v} first, then the {v} closes into the operation</span>
            </div>
          )) : <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>No loop inside this one.</Text>}
        </Field>
        <Field label="When the passes run out (rule-bounded loops)">
          <Input size="small" defaultValue={p.on_exhausted} key={`x-${step.code}`} disabled={readOnly}
                 placeholder="what happens when the rule is never met"
                 onBlur={(e) => e.target.value !== (p.on_exhausted || "") && set({ on_exhausted: e.target.value })} />
        </Field>
      </Section>
    </Flex>
  );
}
